/**
 * StructureIdentityManagement — Admin
 * Identité + Fermetures + SMTP par structure
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Building, Save, Check, Loader, Upload, Trash2, Mail, Calendar, Plus, X, AlertCircle, CheckCircle } from 'lucide-react';
import {
  getStructures, getStructureIdentity, upsertStructureIdentity,
  uploadStructureLogo, deleteStructureLogo, testStructureSmtp,
  getStructureClosures, createStructureClosure, updateStructureClosure, deleteStructureClosure,
} from '../../api';

const EMPTY = {
  siret: '', phone: '', email: '', legalForm: '',
  directorName: '', conventionIdcc: 'IDCC 3127',
  hrReferenceAddress: '', fullName: '', logoUrl: '',
  // Entité juridique
  isLegalEmployer: false, commercialName: '', website: '',
  rcsNumber: '', rcsCity: '', nafCode: '',
  headquartersAddress: '', representativeRole: '',
  // SMTP
  smtpHost: '', smtpPort: 465, smtpSecure: true,
  smtpUser: '', smtpPassword: '', smtpFrom: '', smtpFromName: '',
};

const EMPTY_CLOSURE = {
  startDate: '', endDate: '', label: '', teamWorking: false, notes: '',
  applyToStructureIds: [],
};

const fmtDate = (d) => !d ? '' : new Date(d).toLocaleDateString('fr-FR');

const StructureIdentityManagement = () => {
  const [structures, setStructures] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // SMTP test
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);

  // Closures
  const [closures, setClosures] = useState([]);
  const [loadingClosures, setLoadingClosures] = useState(false);
  const [showClosureForm, setShowClosureForm] = useState(false);
  const [editingClosureId, setEditingClosureId] = useState(null);
  const [closureForm, setClosureForm] = useState(EMPTY_CLOSURE);

  const fetchStructures = useCallback(async () => {
    const res = await getStructures();
    setStructures(res.data || []);
    if (res.data?.length > 0) setSelectedId(res.data[0].id);
  }, []);

  useEffect(() => { fetchStructures(); }, [fetchStructures]);

  const fetchIdentity = useCallback(async () => {
    if (!selectedId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getStructureIdentity(selectedId);
      setForm({ ...EMPTY, ...(res.data || {}) });
    } catch (e) {
      if (e.response?.status === 404) setForm(EMPTY);
      else setError('Erreur de chargement');
    } finally { setLoading(false); }
  }, [selectedId]);

  useEffect(() => { fetchIdentity(); }, [fetchIdentity]);

  const fetchClosures = useCallback(async () => {
    if (!selectedId) return;
    setLoadingClosures(true);
    try {
      const res = await getStructureClosures(selectedId);
      setClosures(res.data || []);
    } catch { setClosures([]); }
    setLoadingClosures(false);
  }, [selectedId]);

  useEffect(() => { fetchClosures(); }, [fetchClosures]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSaved(false);
    try {
      await upsertStructureIdentity(selectedId, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      await fetchIdentity();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally { setLoading(false); }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    setSmtpTestResult(null);
    try {
      const res = await testStructureSmtp(selectedId);
      setSmtpTestResult(res.data);
    } catch (e) {
      setSmtpTestResult({ success: false, message: e.response?.data?.message || e.message });
    }
    setTestingSmtp(false);
  };

  const openClosureForm = (closure = null) => {
    if (closure) {
      setEditingClosureId(closure.id);
      setClosureForm({
        startDate: closure.startDate?.slice(0, 10) || '',
        endDate: closure.endDate?.slice(0, 10) || '',
        label: closure.label || '',
        teamWorking: !!closure.teamWorking,
        notes: closure.notes || '',
        applyToStructureIds: [],
        _groupId: closure.groupId || null,
        _linkedCount: closure.linkedStructures?.length || 0,
      });
    } else {
      setEditingClosureId(null);
      setClosureForm(EMPTY_CLOSURE);
    }
    setShowClosureForm(true);
  };

  const saveClosure = async (e) => {
    e.preventDefault();
    try {
      if (editingClosureId) {
        // Si la fermeture fait partie d'un groupe, demander la portee
        let applyToGroup = false;
        if (closureForm._groupId && closureForm._linkedCount > 0) {
          applyToGroup = window.confirm(
            `Cette fermeture est partagée avec ${closureForm._linkedCount} autre(s) structure(s).\n\nOK = modifier sur TOUTES les structures liées\nAnnuler = modifier uniquement celle-ci`
          );
        }
        await updateStructureClosure(selectedId, editingClosureId, { ...closureForm, applyToGroup });
      } else {
        await createStructureClosure(selectedId, closureForm);
      }
      setShowClosureForm(false);
      await fetchClosures();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const removeClosure = async (closure) => {
    let applyToGroup = false;
    if (closure.groupId && closure.linkedStructures?.length > 0) {
      const choice = window.confirm(
        `Cette fermeture est partagée avec ${closure.linkedStructures.length} autre(s) structure(s).\n\nOK = supprimer sur TOUTES les structures liées\nAnnuler = supprimer uniquement celle-ci`
      );
      applyToGroup = choice;
    } else if (!window.confirm('Supprimer cette fermeture ?')) {
      return;
    }
    try {
      await deleteStructureClosure(selectedId, closure.id, applyToGroup);
      await fetchClosures();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur suppression');
    }
  };

  const toggleApplyTo = (sid) => {
    setClosureForm(p => ({
      ...p,
      applyToStructureIds: p.applyToStructureIds.includes(sid)
        ? p.applyToStructureIds.filter(x => x !== sid)
        : [...p.applyToStructureIds, sid],
    }));
  };

  const otherStructures = structures.filter(s => s.id !== selectedId);

  const selectedStructure = structures.find(s => s.id === selectedId);
  const today = new Date();
  const upcomingClosures = closures.filter(c => new Date(c.endDate) >= today);
  const pastClosures = closures.filter(c => new Date(c.endDate) < today);

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      {/* Header + selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building size={20} />
            Identité des structures
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            Ces informations sont utilisées dans les rapports, CV et analyses RH.
            L'adresse de référence sert à calculer le score localisation des candidatures.
          </p>

          {structures.length > 1 && (
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">Structure</label>
              <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
                className="w-full max-w-xs border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary">
                {structures.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.type && s.type !== 'creche' ? ` (${s.type === 'siege' ? 'Siège' : 'Autre'})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedStructure && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border flex items-center gap-3">
              <div className="flex-1">
                <p className="text-xs text-gray-500">Structure sélectionnée</p>
                <p className="font-semibold text-gray-900">{selectedStructure.name}</p>
              </div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                selectedStructure.type === 'siege' ? 'bg-indigo-100 text-indigo-700' :
                selectedStructure.type === 'autre' ? 'bg-gray-100 text-gray-600' :
                'bg-primary/10 text-primary-dark'
              }`}>
                {selectedStructure.type === 'siege' ? 'Siège' : selectedStructure.type === 'autre' ? 'Autre' : 'Crèche'}
              </span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
          )}

          {loading && !form.siret ? (
            <div className="flex justify-center py-8"><Loader size={24} className="animate-spin text-primary" /></div>
          ) : (
            <form onSubmit={handleSave} className="space-y-5">
              {/* --- Identité --- */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">SIRET</label>
                  <input type="text" placeholder="12345678900012" value={form.siret || ''}
                    onChange={e => setForm(p => ({ ...p, siret: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Forme juridique</label>
                  <input type="text" placeholder="SAS, SARL, Association..." value={form.legalForm || ''}
                    onChange={e => setForm(p => ({ ...p, legalForm: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nom complet de la structure</label>
                  <input type="text" placeholder="Ex: Ô P'TITES QUENOTTES de CHOUPI" value={form.fullName || ''}
                    onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Logo de la structure</label>
                  {form.logoUrl ? (
                    <div className="flex items-center gap-3">
                      <img src={form.logoUrl} alt="Logo" className="h-12 rounded border" />
                      <button type="button" onClick={async () => {
                        try {
                          await deleteStructureLogo(selectedId);
                          setForm(p => ({ ...p, logoUrl: '' }));
                        } catch {}
                      }} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                        <Trash2 size={12} /> Supprimer
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 cursor-pointer px-3 py-2 border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-500">
                      <Upload size={14} /> Uploader un logo (PNG, JPG, SVG, max 2 Mo)
                      <input type="file" accept=".png,.jpg,.jpeg,.svg" className="hidden" onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const fd = new FormData();
                        fd.append('logo', file);
                        try {
                          const res = await uploadStructureLogo(selectedId, fd);
                          setForm(p => ({ ...p, logoUrl: res.data.logoUrl }));
                        } catch {}
                      }} />
                    </label>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nom du directeur / gérant</label>
                  <input type="text" placeholder="Prénom Nom" value={form.directorName || ''}
                    onChange={e => setForm(p => ({ ...p, directorName: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Convention collective</label>
                  <input type="text" placeholder="IDCC 3127 — Services à la personne" value={form.conventionIdcc || ''}
                    onChange={e => setForm(p => ({ ...p, conventionIdcc: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Téléphone</label>
                  <input type="tel" placeholder="04 72 XX XX XX" value={form.phone || ''}
                    onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" placeholder="contact@structure.fr" value={form.email || ''}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Adresse de référence RH
                    <span className="ml-1 text-gray-400 font-normal">(utilisée pour le score localisation des CV)</span>
                  </label>
                  <input type="text" placeholder="63 rue Gervais Bussière, 69100 Villeurbanne" value={form.hrReferenceAddress || ''}
                    onChange={e => setForm(p => ({ ...p, hrReferenceAddress: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                </div>
              </div>

              {/* --- Entité juridique (documents RH : promesse, contrat, avenant) --- */}
              <div className="border-t pt-5">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-semibold text-gray-800 text-sm">Entité juridique</h3>
                  <span className="text-xs text-gray-400 font-normal">— documents RH (promesse, contrat, avenant)</span>
                </div>

                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.isLegalEmployer}
                      onChange={e => setForm(p => ({ ...p, isLegalEmployer: e.target.checked }))}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-amber-900">Cette structure est l'employeur juridique</div>
                      <div className="text-xs text-amber-800 mt-0.5">
                        Cocher uniquement pour la holding / coquille juridique (ex : MC JDE). Ses coordonnées seront utilisées pour l'en-tête et le bloc « Entre les soussignés » de tous les documents RH générés.
                      </div>
                    </div>
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Nom commercial / marque</label>
                    <input type="text" placeholder="Ô p'tites Quenottes" value={form.commercialName || ''}
                      onChange={e => setForm(p => ({ ...p, commercialName: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                    <p className="text-[10px] text-gray-400 mt-1">Apparaît sous le nom de la société dans l'en-tête.</p>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Adresse du siège social</label>
                    <input type="text" placeholder="33 rue des Charmettes, 69100 Villeurbanne" value={form.headquartersAddress || ''}
                      onChange={e => setForm(p => ({ ...p, headquartersAddress: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                    <p className="text-[10px] text-gray-400 mt-1">Utilisée pour la formule « Dont le siège social est situé... » du contrat.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Site internet</label>
                    <input type="text" placeholder="www.optitesquenottes.com" value={form.website || ''}
                      onChange={e => setForm(p => ({ ...p, website: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Rôle du représentant</label>
                    <input type="text" placeholder="Président, Gérant..." value={form.representativeRole || ''}
                      onChange={e => setForm(p => ({ ...p, representativeRole: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                    <p className="text-[10px] text-gray-400 mt-1">Pour « Représentée par X, en sa qualité de Y ».</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">N° RCS</label>
                    <input type="text" placeholder="951 363 837" value={form.rcsNumber || ''}
                      onChange={e => setForm(p => ({ ...p, rcsNumber: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Ville du RCS</label>
                    <input type="text" placeholder="Lyon" value={form.rcsCity || ''}
                      onChange={e => setForm(p => ({ ...p, rcsCity: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Code NAF / APE</label>
                    <input type="text" placeholder="8891A" value={form.nafCode || ''}
                      onChange={e => setForm(p => ({ ...p, nafCode: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
              </div>

              {/* --- SMTP --- */}
              <div className="border-t pt-5">
                <div className="flex items-center gap-2 mb-3">
                  <Mail size={16} className="text-primary" />
                  <h3 className="font-semibold text-gray-800 text-sm">Email & SMTP</h3>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Configuration SMTP pour que la structure envoie ses propres emails (arrêt de travail au cabinet RH, notifications...).
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Serveur SMTP</label>
                    <input type="text" placeholder="smtp.titan.email" value={form.smtpHost || ''}
                      onChange={e => setForm(p => ({ ...p, smtpHost: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Port</label>
                      <input type="number" placeholder="465" value={form.smtpPort || ''}
                        onChange={e => setForm(p => ({ ...p, smtpPort: e.target.value ? Number(e.target.value) : '' }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={form.smtpSecure !== false}
                          onChange={e => setForm(p => ({ ...p, smtpSecure: e.target.checked }))}
                          className="rounded" />
                        SSL/TLS
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Utilisateur SMTP</label>
                    <input type="text" placeholder="contact@opq-bibou.fr" value={form.smtpUser || ''}
                      onChange={e => setForm(p => ({ ...p, smtpUser: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Mot de passe SMTP</label>
                    <input type="password" placeholder="••••••••"
                      value={form.smtpPassword || ''}
                      onChange={e => setForm(p => ({ ...p, smtpPassword: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                    <p className="text-[10px] text-gray-400 mt-0.5">Laisser masqué pour conserver l'actuel</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Expéditeur (from)</label>
                    <input type="email" placeholder="contact@opq-bibou.fr" value={form.smtpFrom || ''}
                      onChange={e => setForm(p => ({ ...p, smtpFrom: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Nom expéditeur</label>
                    <input type="text" placeholder="Micro-crèche Bibou" value={form.smtpFromName || ''}
                      onChange={e => setForm(p => ({ ...p, smtpFromName: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  </div>
                </div>

                {smtpTestResult && (
                  <div className={`mt-4 p-3 rounded-lg text-sm flex items-start gap-2 ${
                    smtpTestResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {smtpTestResult.success ? <CheckCircle size={16} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />}
                    <span>{smtpTestResult.message}</span>
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2">
                  <button type="button" onClick={handleTestSmtp} disabled={testingSmtp || !form.smtpHost}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
                    {testingSmtp ? <Loader size={14} className="animate-spin" /> : <Mail size={14} />}
                    Tester la connexion
                  </button>
                  <span className="text-xs text-gray-400">Envoie un mail test à l'expéditeur</span>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button type="submit" disabled={loading || !selectedId}
                  className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 transition-colors">
                  {loading ? <Loader size={16} className="animate-spin" /> : saved ? <Check size={16} /> : <Save size={16} />}
                  {saved ? 'Enregistré !' : 'Enregistrer'}
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* --- Fermetures --- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar size={20} />
            Périodes de fermeture
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            Jours fériés, ponts, fermetures estivales... Ces jours génèrent des entrées <strong>CP (congé payé)</strong> par défaut dans les nouvelles feuilles de temps, modifiables ensuite.
            Cocher <strong>« Journée pédagogique »</strong> pour générer des entrées de travail normal à la place (crèche fermée aux familles mais équipe présente).
          </p>

          {showClosureForm && (
            <div className="mb-4 p-4 bg-gray-50 border rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-sm text-gray-800">{editingClosureId ? 'Modifier' : 'Nouvelle'} fermeture</h4>
                <button onClick={() => setShowClosureForm(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
              </div>
              <form onSubmit={saveClosure} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Date début *</label>
                    <input type="date" required value={closureForm.startDate}
                      onChange={e => setClosureForm(p => ({ ...p, startDate: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Date fin *</label>
                    <input type="date" required value={closureForm.endDate}
                      onChange={e => setClosureForm(p => ({ ...p, endDate: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Libellé *</label>
                  <input type="text" required placeholder="Ex: Fermeture d'été, Pont du 1er mai..." value={closureForm.label}
                    onChange={e => setClosureForm(p => ({ ...p, label: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={closureForm.teamWorking}
                      onChange={e => setClosureForm(p => ({ ...p, teamWorking: e.target.checked }))}
                      className="mt-0.5 rounded" />
                    <span>
                      <span className="font-medium">Journée pédagogique</span>
                      <span className="block text-xs text-gray-500">
                        Coché : crèche fermée aux familles mais l'équipe travaille (formation, réunion, ménage) → entrées <strong>worked</strong> dans les feuilles de temps.<br/>
                        Décoché : fermeture standard (férié, pont, congé) → entrées <strong>CP</strong> par défaut (modifiables ensuite).
                      </span>
                    </span>
                  </label>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optionnel)</label>
                  <textarea value={closureForm.notes} rows={2}
                    onChange={e => setClosureForm(p => ({ ...p, notes: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                </div>

                {/* Multi-structures : uniquement en creation */}
                {!editingClosureId && otherStructures.length > 0 && (
                  <div className="border-t pt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Appliquer aussi à d'autres structures
                      <span className="ml-1 text-gray-400 font-normal">(crée une fermeture liée par groupe)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {otherStructures.map(s => (
                        <label key={s.id} className="flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm cursor-pointer hover:bg-gray-50">
                          <input type="checkbox"
                            checked={closureForm.applyToStructureIds.includes(s.id)}
                            onChange={() => toggleApplyTo(s.id)}
                            className="rounded" />
                          <span>{s.name}</span>
                          {s.type && s.type !== 'creche' && (
                            <span className="text-[10px] text-gray-400">({s.type === 'siege' ? 'Siège' : 'Autre'})</span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {editingClosureId && closureForm._linkedCount > 0 && (
                  <div className="border-t pt-3 bg-amber-50/50 -mx-4 px-4 py-2 text-xs text-amber-800">
                    ⚠️ Cette fermeture est partagée avec {closureForm._linkedCount} autre(s) structure(s).
                    À l'enregistrement, tu pourras choisir de modifier uniquement celle-ci ou toutes les liées.
                  </div>
                )}

                <div className="flex gap-2">
                  <button type="submit" className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary-dark">
                    <Save size={14} />
                    {editingClosureId ? 'Mettre à jour' : 'Créer'}
                  </button>
                  <button type="button" onClick={() => setShowClosureForm(false)} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100">
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          )}

          {!showClosureForm && (
            <button onClick={() => openClosureForm()}
              className="mb-4 flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark">
              <Plus size={14} /> Ajouter une fermeture
            </button>
          )}

          {loadingClosures ? (
            <div className="flex justify-center py-4"><Loader size={20} className="animate-spin text-primary" /></div>
          ) : closures.length === 0 ? (
            <p className="text-center py-6 text-gray-400 text-sm">Aucune fermeture enregistrée</p>
          ) : (
            <div className="space-y-4">
              {upcomingClosures.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">À venir ou en cours</h4>
                  <ClosureTable closures={upcomingClosures} onEdit={openClosureForm} onDelete={removeClosure} />
                </div>
              )}
              {pastClosures.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Passées</h4>
                  <ClosureTable closures={pastClosures} onEdit={openClosureForm} onDelete={removeClosure} faded />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const ClosureTable = ({ closures, onEdit, onDelete, faded }) => (
  <div className={`border rounded-lg overflow-hidden ${faded ? 'opacity-60' : ''}`}>
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b">
        <tr>
          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Période</th>
          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Libellé</th>
          <th className="text-center px-3 py-2 text-xs font-semibold text-gray-600">Type</th>
          <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600 w-24">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {closures.map(c => (
          <tr key={c.id} className="hover:bg-gray-50">
            <td className="px-3 py-2 text-gray-700">
              {fmtDate(c.startDate)} → {fmtDate(c.endDate)}
            </td>
            <td className="px-3 py-2">
              <div className="font-medium text-gray-800 flex items-center gap-2">
                {c.label}
                {c.linkedStructures?.length > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700" title={c.linkedStructures.map(s => s.name).join(', ')}>
                    Lié à {c.linkedStructures.length} autre{c.linkedStructures.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {c.notes && <div className="text-xs text-gray-500">{c.notes}</div>}
            </td>
            <td className="px-3 py-2 text-center">
              {c.teamWorking ? (
                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">Journée pédagogique</span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">CP</span>
              )}
            </td>
            <td className="px-3 py-2 text-right">
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => onEdit(c)} className="p-1 text-gray-500 hover:text-primary rounded" title="Modifier">
                  <Save size={14} />
                </button>
                <button onClick={() => onDelete(c)} className="p-1 text-gray-500 hover:text-red-600 rounded" title="Supprimer">
                  <Trash2 size={14} />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default StructureIdentityManagement;
